import {
	ClipboardDocumentCheckIcon,
	HomeIcon,
	ListBulletIcon,
	MapPinIcon,
	PencilSquareIcon,
	ShieldCheckIcon,
	TagIcon,
	UsersIcon,
} from "@heroicons/react/24/outline";
import { Link } from "gatsby";
import React from "react";
import { useAuthContext } from "../../../contexts/AuthProvider";
import Apply from "./icons/Apply";
import Check from "./icons/Check";
import CircleIcon from "./icons/CircleIcon";
import Complaint from "./icons/Claim";
import CircleExclamationIcon from "./icons/ExclamationIcon";
import NewIcon from "./icons/NewIcon";
import RequestIcon from "./icons/RequestIcon";

function SidebarLink({ to, icon: Icon, children, iconComponent, onClick }) {
	const isActive =
		typeof window !== "undefined" &&
		window.location.pathname.replace(/\/$/, "") === to.replace(/\/$/, "");
	return (
		<Link
			to={to}
			onClick={onClick}
			className={`group flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg text-sm font-medium no-underline transition-all duration-200
        ${
					isActive
						? "bg-blue-600/15 text-blue-400 shadow-sm"
						: "text-gray-400 hover:bg-gray-700/50 hover:text-gray-100"
				}`}
		>
			<span
				className={`flex-shrink-0 transition-colors duration-200 ${isActive ? "text-blue-400" : "text-gray-500 group-hover:text-gray-300"}`}
			>
				{iconComponent ? (
					iconComponent
				) : Icon ? (
					<Icon className="w-5 h-5" />
				) : null}
			</span>
			<span className="truncate">{children}</span>
		</Link>
	);
}

function SidebarSection({ label, children }) {
	return (
		<div className="mt-5 first:mt-0">
			<h3 className="mb-2 px-5 font-semibold text-[11px] text-gray-500 uppercase tracking-wider">
				{label}
			</h3>
			<div className="space-y-0.5">{children}</div>
		</div>
	);
}

export default function Sidebar({ isOpen, onClose }) {
	const { user } = useAuthContext();

	return (
		<div
			className={`fixed top-[var(--admin-navbar-height)] left-0 z-40 flex flex-col w-[260px] h-[calc(100vh-var(--admin-navbar-height))] overflow-y-auto border-r border-gray-800/60 bg-gray-900 text-white sidebar transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
		>
			{/* Navigation */}
			<div className="space-y-1 w-full px-1 pt-6 pb-6">
				<SidebarSection label="Overview">
					<SidebarLink to="/admin" icon={HomeIcon} onClick={onClose}>
						Dashboard
					</SidebarLink>
				</SidebarSection>

				{(user?.all_permissions?.includes("manage_users") ||
					user?.all_permissions?.includes("manage_categories") ||
					user?.all_permissions?.includes("manage_products") ||
					user?.all_permissions?.includes("manage_locations")) && (
					<SidebarSection label="Users & Catalog">
						{user?.all_permissions?.includes("manage_users") && (
							<>
								<SidebarLink
									to="/admin/users"
									icon={UsersIcon}
									onClick={onClose}
								>
									Users
								</SidebarLink>
								{user?.authority >= 5 && (
									<SidebarLink
										to="/admin/permission-groups"
										icon={ShieldCheckIcon}
										onClick={onClose}
									>
										Permission Groups
									</SidebarLink>
								)}
								<SidebarLink
									to="/admin/users-requests"
									iconComponent={<RequestIcon className="w-5 h-5" />}
									onClick={onClose}
								>
									User Requests
								</SidebarLink>
							</>
						)}
						{user?.all_permissions?.includes("manage_categories") && (
							<SidebarLink
								to="/admin/categories"
								icon={TagIcon}
								onClick={onClose}
							>
								Categories
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_products") && (
							<SidebarLink
								to="/admin/products"
								icon={ListBulletIcon}
								onClick={onClose}
							>
								Products
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_locations") && (
							<SidebarLink
								to="/admin/locations"
								icon={MapPinIcon}
								onClick={onClose}
							>
								Locations
							</SidebarLink>
						)}
					</SidebarSection>
				)}

				{(user?.all_permissions?.includes("manage_applications") ||
					user?.all_permissions?.includes("manage_checks") ||
					user?.all_permissions?.includes("manage_feedbacks") ||
					user?.all_permissions?.includes("manage_complaints") ||
					user?.all_permissions?.includes("manage_questions")) && (
					<SidebarSection label="Management">
						{user?.all_permissions?.includes("manage_applications") && (
							<>
								<SidebarLink
									to="/admin/applies"
									iconComponent={
										<Apply className="w-5 h-5" aria-hidden="true" />
									}
									onClick={onClose}
								>
									Applies
								</SidebarLink>
								<SidebarLink
									to="/admin/saudi-applies"
									iconComponent={<NewIcon className="w-5 h-5" />}
									onClick={onClose}
								>
									Saudi Applications
								</SidebarLink>
							</>
						)}
						{user?.all_permissions?.includes("manage_checks") && (
							<SidebarLink
								to="/admin/checks"
								iconComponent={<Check className="w-5 h-5" />}
								onClick={onClose}
							>
								Checks
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_feedbacks") && (
							<SidebarLink
								to="/admin/feedbacks"
								iconComponent={<Complaint className="w-5 h-5" />}
								onClick={onClose}
							>
								Feedbacks
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_complaints") && (
							<SidebarLink
								to="/admin/complaints"
								iconComponent={<CircleExclamationIcon className="w-5 h-5" />}
								onClick={onClose}
							>
								Complaints
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_questions") && (
							<SidebarLink
								to="/admin/questions"
								iconComponent={<CircleIcon className="w-5 h-5" />}
								onClick={onClose}
							>
								Questions
							</SidebarLink>
						)}
					</SidebarSection>
				)}

				{(user?.all_permissions?.includes("manage_certificates") ||
					user?.all_permissions?.includes("manage_blogs")) && (
					<SidebarSection label="Content">
						{user?.all_permissions?.includes("manage_certificates") && (
							<SidebarLink
								to="/admin/certificates"
								icon={ClipboardDocumentCheckIcon}
								onClick={onClose}
							>
								Certificates
							</SidebarLink>
						)}
						{user?.all_permissions?.includes("manage_blogs") && (
							<SidebarLink
								to="/admin/blogs"
								icon={PencilSquareIcon}
								onClick={onClose}
							>
								Blog
							</SidebarLink>
						)}
					</SidebarSection>
				)}
			</div>
		</div>
	);
}
